import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Search, ArrowUpRight, ArrowDownLeft, Minus, Globe, ChevronRight, ChevronDown } from "lucide-react";
import {
  fetchUserGeositeTree,
  setGeositeRouting,
  previewRouting,
  type UserTreeNode,
  type PreviewResult,
} from "@/api/geosite";
import { apiErrorMessage } from "@/api/axios";

function TreeNodeRender({
  node,
  map,
  depth,
  expanded,
  toggle,
  setRouting,
  toggling,
}: {
  node: UserTreeNode;
  map: Map<string, UserTreeNode>;
  depth: number;
  expanded: Set<string>;
  toggle: (name: string) => void;
  setRouting: (name: string, routing: string) => void;
  toggling: string | null;
}) {
  const isExpanded = expanded.has(node.name);
  const hasChildren = node.children.length > 0;
  const currentRouting = node.user_routing || node.default_routing;
  const isAuto = !node.user_routing;

  return (
    <div>
      <div
        className="flex items-center gap-2 py-2 px-3 hover:bg-slate-800/30 border-b border-slate-800/20"
        style={{ paddingLeft: `${12 + depth * 20}px` }}
      >
        {hasChildren ? (
          <span onClick={(e) => { e.stopPropagation(); toggle(node.name); }} className="cursor-pointer">
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-slate-500" />
            )}
          </span>
        ) : (
          <span className="w-3.5" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-slate-200">{node.name}</span>
            {!isAuto && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">
                переопределено
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {node.domain_count.toLocaleString()} доменов
            {isAuto && (
              <span className="ml-2 text-slate-400">
                → {node.default_routing === "vpn" ? "Secure" : "Direct"}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            size="sm"
            variant={isAuto ? "default" : "ghost"}
            className={isAuto ? "bg-slate-700 text-slate-200 hover:bg-slate-600" : "text-slate-400 hover:text-slate-200"}
            onClick={() => setRouting(node.name, "auto")}
            disabled={toggling === node.name}
          >
            <Minus className="h-3.5 w-3.5 mr-1" />
            Auto
          </Button>
          <Button
            size="sm"
            variant={currentRouting === "direct" ? "default" : "ghost"}
            className={
              currentRouting === "direct"
                ? "bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30"
                : "text-slate-400 hover:text-slate-200"
            }
            onClick={() => setRouting(node.name, "direct")}
            disabled={toggling === node.name}
          >
            <ArrowDownLeft className="h-3.5 w-3.5 mr-1" />
            Direct
          </Button>
          <Button
            size="sm"
            variant={currentRouting === "vpn" ? "default" : "ghost"}
            className={
              currentRouting === "vpn"
                ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30"
                : "text-slate-400 hover:text-slate-200"
            }
            onClick={() => setRouting(node.name, "vpn")}
            disabled={toggling === node.name}
          >
            <ArrowUpRight className="h-3.5 w-3.5 mr-1" />
            Secure
          </Button>
        </div>
      </div>
      {isExpanded &&
        node.children.map((childName) => {
          const child = map.get(childName);
          if (!child) return null;
          return (
            <TreeNodeRender
              key={childName}
              node={child}
              map={map}
              depth={depth + 1}
              expanded={expanded}
              toggle={toggle}
              setRouting={setRouting}
              toggling={toggling}
            />
          );
        })}
    </div>
  );
}

export function GeositeCatalog() {
  const [tree, setTree] = useState<UserTreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [toggling, setToggling] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [previewDomain, setPreviewDomain] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null);

  const load = async () => {
    try {
      const res = await fetchUserGeositeTree();
      setTree(res.data.categories || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    fetchUserGeositeTree()
      .then((res) => {
        if (mounted) setTree(res.data.categories || []);
      })
      .catch((err) => {
        if (mounted) console.error(err);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const setRouting = async (name: string, routing: string) => {
    setToggling(name);
    try {
      await setGeositeRouting(name, routing);
      await load();
    } catch (e: unknown) {
      alert(apiErrorMessage(e, "Ошибка"));
    } finally {
      setToggling(null);
    }
  };

  const handlePreview = async () => {
    if (!previewDomain.trim()) return;
    setPreviewLoading(true);
    try {
      const res = await previewRouting(previewDomain.trim());
      setPreviewResult(res.data);
    } catch (e: unknown) {
      alert(apiErrorMessage(e, "Ошибка проверки"));
    } finally {
      setPreviewLoading(false);
    }
  };

  const map = new Map<string, UserTreeNode>();
  const allChildren = new Set<string>();
  for (const n of tree) {
    map.set(n.name, n);
    for (const c of n.children) allChildren.add(c);
  }

  const toggle = (name: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      {/* Preview checker */}
      <Card className="border-slate-800 bg-slate-900">
        <CardContent className="pt-4 space-y-3">
          <div className="flex items-center gap-2 text-sm text-slate-300">
            <Globe className="h-4 w-4 text-slate-400" />
            <span className="font-medium">Проверка маршрутизации домена</span>
          </div>
          <div className="flex items-center gap-2">
            <Input
              placeholder="youtube.com"
              value={previewDomain}
              onChange={(e) => setPreviewDomain(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handlePreview()}
              className="bg-slate-950 border-slate-800"
            />
            <Button size="sm" onClick={handlePreview} disabled={previewLoading}>
              {previewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Проверить"}
            </Button>
          </div>
          {previewResult && (
            <div className="text-sm space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-slate-300">{previewResult.domain}</span>
                <span className="text-slate-500">→</span>
                <span
                  className={`font-medium ${
                    previewResult.decision === "vpn" ? "text-emerald-400" : "text-blue-400"
                  }`}
                >
                  {previewResult.decision === "vpn" ? "SECURE" : previewResult.decision.toUpperCase()}
                </span>
                <span className="text-xs text-slate-500">({previewResult.reason})</span>
              </div>
              {previewResult.matched_categories.length > 0 && (
                <div className="text-xs text-slate-500">
                  Категории: {previewResult.matched_categories.map((c) => c.name).join(", ")}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Search */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            placeholder="Поиск категории..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-slate-950 border-slate-800"
          />
        </div>
        {search && (
          <Button size="sm" variant="ghost" onClick={() => setSearch("")} className="text-slate-400">
            Сбросить
          </Button>
        )}
      </div>

      {/* Tree */}
      {loading && tree.length === 0 ? (
        <div className="text-center text-slate-400 py-8">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
          Загрузка...
        </div>
      ) : (
        <div className="border border-slate-800 rounded-md overflow-hidden max-h-[70vh] overflow-y-auto">
          {(() => {
            if (tree.length === 0) {
              return <div className="py-8 text-center text-slate-500">Нет категорий</div>;
            }
            if (search.trim()) {
              const q = search.toLowerCase();
              const matches = tree.filter((n) => n.name.toLowerCase().includes(q));
              if (matches.length === 0) {
                return <div className="py-8 text-center text-slate-500">Ничего не найдено</div>;
              }
              return (
                <div>
                  {matches.map((node) => (
                    <TreeNodeRender
                      key={node.name}
                      node={node}
                      map={map}
                      depth={0}
                      expanded={expanded}
                      toggle={toggle}
                      setRouting={setRouting}
                      toggling={toggling}
                    />
                  ))}
                </div>
              );
            }
            const roots = tree
              .filter((n) => !allChildren.has(n.name))
              .sort((a, b) => a.name.localeCompare(b.name));
            return (
              <div>
                {roots.map((node) => (
                  <TreeNodeRender
                    key={node.name}
                    node={node}
                    map={map}
                    depth={0}
                    expanded={expanded}
                    toggle={toggle}
                    setRouting={setRouting}
                    toggling={toggling}
                  />
                ))}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
